import * as datGui from 'dat.gui'
import {
  Scene,
  Camera,
  Renderer,
  Object3D,
  DirectionalLightHelper,
  PointLightHelper,
  PointLight,
  DirectionalLight,
} from 'three'
import { TransformControls } from 'three/examples/jsm/controls/TransformControls'
import { isDirectionalLight, isPointLight } from '../../utils/type-guards'
import { StoreScheme } from '../../../shared/store/store'

export function createControlsGui(
  targetFolder: datGui.GUI,
  name: string,
  object: Object3D | PointLight | DirectionalLight,
  scene: Scene,
  camera: Camera,
  renderer: Renderer,
  store: StoreScheme,
  defaultState = false,
) {
  const guiHelperObject = { visible: defaultState }
  const controlObjects = createControlsObjects(object, scene, camera, renderer, store)
  const controlsGui = targetFolder
    .add(guiHelperObject, 'visible')
    .name(name)
    .onChange(controlObjects.changeControlObjectVisible)
  controlObjects.changeControlObjectVisible(defaultState)
  return controlsGui
}

function createControlsObjects(
  object: Object3D | PointLight | DirectionalLight,
  scene: Scene,
  camera: Camera,
  renderer: Renderer,
  store: StoreScheme,
) {
  const internalObj = isDirectionalLight(object)
    ? createControlsObjects(object.target, scene, camera, renderer, store)
    : undefined

  const helperObject = createHelperObject(object)
  const controls = new TransformControls(camera, renderer.domElement)
  scene.add(controls)

  changeModeOnHotKey()
  stopControlsOnMove()
  updateHelperOnChange()

  if (helperObject) {
    scene.add(helperObject)
    helperObject.parent?.updateMatrixWorld()
    helperObject.update()
  }

  const changeControlObjectVisible = (value: boolean) => {
    if (value) {
      controls.attach(object)
    } else {
      controls.detach()
    }

    if (helperObject) {
      helperObject.visible = value
    } else {
      object.visible = value
    }
    internalObj?.changeControlObjectVisible(value)
  }

  return { changeControlObjectVisible, helperObject, controls }

  function updateHelperOnChange() {
    controls.addEventListener('change', updateHelper)
    internalObj?.controls.addEventListener('change', updateHelper)

    function updateHelper() {
      if (helperObject && helperObject.visible) {
        helperObject.parent?.updateMatrixWorld()
        helperObject.update()
      }
    }
  }

  function stopControlsOnMove() {
    controls.addEventListener('dragging-changed', (event) => {
      const cameraController = store.getState().cameraController
      if (!cameraController) return
      if (event.value === true) cameraController.stop()
      else cameraController.start()
    })
  }

  function changeModeOnHotKey() {
    window.addEventListener('keydown', function (event) {
      switch (event.key) {
        case 'q':
          controls.setSpace(controls.space === 'local' ? 'world' : 'local')
          break
        case 'w':
          controls.setMode('translate')
          break
        case 'e':
          controls.setMode('rotate')
          break
      }
    })
  }
}

function createHelperObject(object: Object3D | PointLight | DirectionalLight) {
  if (isPointLight(object)) {
    return new PointLightHelper(object, 0.5)
  } else if (isDirectionalLight(object)) {
    return new DirectionalLightHelper(object, 0.5)
  }
}
